/**
 * @file AsanaIntegrationPage.tsx
 * @brief Company Admin → Settings → Asana, and a personal user's own Asana
 *        (/app/integrations/asana). Connect, choose a destination, map people.
 *
 * @details
 * Three jobs, in the order they must be done, and the page refuses to show a
 * later one before the earlier one is satisfied — a project picker above an
 * unconnected account is a dead control that teaches people the page is broken.
 *
 * The people table is the part worth the effort. Matching assignees by email
 * alone fails constantly in the real world (work vs personal addresses, alias
 * domains, contractors) and it fails **invisibly**: the push succeeds and tasks
 * simply arrive unassigned, with nobody able to say why. Here the gap is
 * visible before anything is pushed, and fixable in one click.
 *
 * Visual language: Meeting Intel / dashboard GridBlock washes — brand hero,
 * status strip, KPI cells, soft section bands — not a flat form on white.
 *
 * @dependencies
 * - ./api, ./schemas
 * - features/analytics/components/GridBlock, StatCard
 * - components/feedback/EmptyState
 *
 * @references
 * - BACKEND/src/features/connectors/routes.js
 * - docs/product/meeting-intelligence.md
 * - docs/architecture/connectors.md
 *
 * @author DES
 * @date 05-08-2026
 * @updated 05-08-2026 — brand washes, KPI strip, status band, people presence
 * @updated 21-10-2026 — personal users (no company) reach it from Profile →
 *   Connected apps: the back link goes there, and the people matching (a
 *   company job) is hidden for them (DES).
 */

import { SkeletonFields, SkeletonRows } from "@/components/ui/Skeleton";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/auth/auth-context";
import {
  Building2,
  CircleAlert,
  CircleCheck,
  ExternalLink,
  FolderKanban,
  Link2,
  Link2Off,
  ListTodo,
  LoaderCircle,
  RefreshCw,
  Users,
} from "lucide-react";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatCard } from "@/features/analytics/components/StatCard";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { cn } from "@/shared/theme/cn";
import {
  useAsanaAuthorizeUrl,
  useAsanaPeople,
  useAsanaProjects,
  useAsanaWorkspaces,
  useConnectorStatus,
  useDisconnectAsana,
  useMapAsanaPerson,
  useSetAsanaProject,
  useSetAsanaWorkspace,
} from "./api";
import type { AsanaPerson } from "./schemas";

/** Radix Select rejects empty string — email-fallback is the soft default. */
const MATCH_BY_EMAIL = "__email__";

const compactSelectTriggerClass =
  "h-9 w-full rounded-md border-border bg-background px-3 text-sm font-normal shadow-none";

const SETUP_STEPS = [
  {
    n: "1",
    title: "Connect",
    body: "Link the Asana account that should author the tasks.",
  },
  {
    n: "2",
    title: "Destination",
    body: "Pick the project every meeting to-do lands in.",
  },
  {
    n: "3",
    title: "People",
    body: "Match Verblizr people to Asana members before you push.",
  },
] as const;

function initials(name: string | null | undefined, email: string | null | undefined) {
  const fromName = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  if (fromName) return fromName;
  const fromEmail = (email ?? "").trim().charAt(0).toUpperCase();
  return fromEmail || "?";
}

function MatchBadge({ source }: { source: AsanaPerson["source"] }) {
  if (source === "none") {
    return (
      <Badge variant="warning" className="gap-1 text-[10px] uppercase tracking-wide">
        <CircleAlert className="h-3 w-3" />
        Not matched
      </Badge>
    );
  }
  if (source === "manual") {
    return (
      <Badge variant="success" className="gap-1 text-[10px] uppercase tracking-wide">
        <CircleCheck className="h-3 w-3" />
        Mapped
      </Badge>
    );
  }
  return (
    <Badge variant="info" className="gap-1 text-[10px] uppercase tracking-wide">
      <CircleCheck className="h-3 w-3" />
      By email
    </Badge>
  );
}

function SectionLabel({
  children,
  hint,
  action,
}: {
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <GridBand className="bg-muted/25 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {children}
          </h3>
          {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
        </div>
        {action}
      </div>
    </GridBand>
  );
}

export function AsanaIntegrationPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // A personal account on /app: its own Asana, nobody else to match.
  const solo = pathname.startsWith("/app") && !(user?.company_id ?? user?.companyId);
  const status = useConnectorStatus();
  const asana = status.data?.asana;
  const connected = Boolean(asana?.connected);
  // ADR-051 (21-10-2026): the company has not allowed Asana while sharing is
  // inside-only. The server refuses every call (403 CONNECTOR_NOT_ALLOWED).
  const blocked = status.data?.allowedByCompany === false;
  const blockedMessage =
    status.data?.blockedMessage ||
    "Your company admin has not allowed Asana. Ask them to turn it on in Company settings.";

  const workspaces = useAsanaWorkspaces(connected && !blocked);
  const projects = useAsanaProjects(connected && !blocked);
  const people = useAsanaPeople(connected && !blocked);
  const authorize = useAsanaAuthorizeUrl();
  const setWorkspace = useSetAsanaWorkspace();
  const setProject = useSetAsanaProject();
  const mapPerson = useMapAsanaPerson();
  const disconnectAsana = useDisconnectAsana();
  const [notice, setNotice] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Mapping edits are DRAFTED, not applied on change (05-08-2026)
  //
  // This write has consequences in someone else's system: a mis-picked member
  // silently routes that person's to-dos to the wrong human, with no
  // confirmation and no undo. That is not a display preference, and it should
  // not commit on a stray click. Edits collect here until Save.
  // ------------------------------------------------------------------
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState(false);

  const peopleList = people.data?.people ?? [];
  const members = people.data?.members ?? [];
  const projectOptions = projects.data?.projects ?? [];
  const workspaceOptions = workspaces.data?.workspaces ?? [];
  const selectedWorkspaceId = workspaces.data?.selectedId ?? null;
  // Always shown, even for the single-workspace account it cannot help — there
  // it reads as a statement of fact ("to-dos go here") rather than a choice.
  // Hiding it was worse on both counts: which workspace is in use is worth
  // saying out loud, and a control that appears for only some accounts is one
  // nobody learns to look for. (17-08-2026)
  const canChangeWorkspace = workspaceOptions.length > 1;
  const unmatched = peopleList.filter((p) => p.source === "none").length;
  const matched = peopleList.length - unmatched;
  const projectReady = Boolean(asana?.projectId);

  /** What the row should SHOW: the pending edit if there is one, else stored. */
  const shownMatch = (p: AsanaPerson) =>
    p.userId in draft ? draft[p.userId] : (p.asanaUserId ?? null);

  const dirtyIds = Object.keys(draft).filter((id) => {
    const person = peopleList.find((p) => p.userId === id);
    if (!person) return false;
    // Selecting the value it already had is not a change — treating it as one
    // would arm the Save button (and the leave-warning) over nothing.
    return (draft[id] ?? null) !== (person.asanaUserId ?? null);
  });
  const hasUnsaved = dirtyIds.length > 0;

  const connect = async () => {
    setNotice(null);
    try {
      const url = await authorize.mutateAsync();
      // A new tab, not a redirect: the admin keeps this page open and comes
      // back to a list that is already in the state they left it in.
      window.open(url, "_blank", "noopener,noreferrer");
      setNotice("Approve access in the Asana tab, then press Refresh.");
    } catch (err) {
      setNotice((err as Error)?.message || "Could not start the Asana connection.");
    }
  };

  const refresh = () => {
    void status.refetch();
    if (connected) {
      void workspaces.refetch();
      void projects.refetch();
      void people.refetch();
    }
  };

  const changeWorkspace = async (workspaceId: string) => {
    if (workspaceId === selectedWorkspaceId) return;
    setNotice(null);
    try {
      await setWorkspace.mutateAsync({ workspaceId });
      // Said plainly, because the destination silently emptying is exactly the
      // kind of surprise that makes someone think the page lost their settings.
      setNotice(
        "Workspace changed. Pick a destination project again — the previous one belonged to the old workspace.",
      );
    } catch (err) {
      setNotice((err as Error)?.message || "Could not change the workspace.");
    }
  };

  const saveMappings = async () => {
    setSaving(true);
    setNotice(null);
    try {
      // Sequential: a handful of rows at most, and a partial failure is far
      // easier to reason about than a race of parallel writes.
      for (const userId of dirtyIds) {
        const asanaUserId = draft[userId] ?? null;
        const name = members.find((m) => m.id === asanaUserId)?.name ?? null;
        await mapPerson.mutateAsync({ userId, asanaUserId, asanaUserName: name });
      }
      setDraft({});
      setNotice("Matches saved.");
    } catch {
      setNotice("Could not save every match. Nothing else was changed.");
    } finally {
      setSaving(false);
    }
  };

  // The browser's own guard. Deliberately not a custom modal: this fires for a
  // tab close and a reload too, which a router-level blocker cannot see.
  useEffect(() => {
    if (!hasUnsaved) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved]);

  const serverReady =
    Boolean(status.data?.configured) && Boolean(status.data?.secretStorageReady);
  const showSetup =
    !status.isLoading && !status.isError && serverReady && !connected;

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        {/* ------------------------------------------------------------------ */}
        {/* Hero                                                                */}
        {/* ------------------------------------------------------------------ */}
        <GridBand className="relative overflow-hidden bg-brand-soft/35">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand/10 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 left-1/3 h-40 w-40 rounded-full bg-brand/5 blur-2xl"
          />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 space-y-3">
              <Link
                to={pathname.startsWith("/app") ? "/app/account?tab=apps" : "/company/settings"}
                className="text-xs font-medium text-muted-foreground transition-colors hover:text-brand"
              >
                {pathname.startsWith("/app") ? "← Connected apps" : "← Settings"}
              </Link>
              <div className="flex items-start gap-3.5">
                <span className="flex h-12 shrink-0 items-center text-brand">
                  <ListTodo className="h-5 w-5" />
                </span>
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                      Asana
                    </h2>
                    {connected ? (
                      <Badge
                        variant="success"
                        className="text-[10px] uppercase tracking-wide"
                      >
                        Connected
                      </Badge>
                    ) : serverReady ? (
                      <Badge
                        variant="warning"
                        className="text-[10px] uppercase tracking-wide"
                      >
                        Not connected
                      </Badge>
                    ) : null}
                  </div>
                  <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                    Meeting to-dos become Asana tasks — each one carrying the
                    exact moment it was agreed, so the assignee can jump back to
                    the recording.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={refresh}>
                <RefreshCw
                  className={cn(
                    "mr-2 h-3.5 w-3.5",
                    status.isFetching || people.isFetching ? "animate-spin" : null,
                  )}
                />
                Refresh
              </Button>
              {connected ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => disconnectAsana.mutate()}
                  disabled={disconnectAsana.isPending}
                >
                  <Link2Off className="mr-2 h-3.5 w-3.5" />
                  Disconnect
                </Button>
              ) : null}
              {connected ? (
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="https://app.asana.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Asana
                    <ExternalLink className="ml-2 h-3.5 w-3.5" />
                  </a>
                </Button>
              ) : null}
            </div>
          </div>
        </GridBand>

        {/* ------------------------------------------------------------------ */}
        {/* Loading / error / server gates                                      */}
        {/* ------------------------------------------------------------------ */}
        {status.isLoading ? (
          <GridCell>
            <SkeletonFields className="py-4" />
          </GridCell>
        ) : status.isError ? (
          <GridCell>
            <p className="py-10 text-sm text-destructive">
              Couldn&apos;t load connector status. Press Refresh and try again.
            </p>
          </GridCell>
        ) : status.data && !status.data.configured ? (
          <GridCell className="bg-warning/5">
            <div className="flex items-start gap-3">
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <p className="text-sm text-muted-foreground">
                Asana is not configured on this server. Set{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  ASANA_CLIENT_ID
                </code>
                ,{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  ASANA_CLIENT_SECRET
                </code>{" "}
                and{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  ASANA_REDIRECT_URI
                </code>
                .
              </p>
            </div>
          </GridCell>
        ) : status.data && !status.data.secretStorageReady ? (
          <GridCell className="bg-warning/5">
            <div className="flex items-start gap-3">
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <p className="text-sm text-muted-foreground">
                Secret storage is not configured (
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  CONNECTOR_ENCRYPTION_KEY
                </code>
                ). Connecting is disabled rather than storing your Asana token
                unencrypted.
              </p>
            </div>
          </GridCell>
        ) : (
          <>
            {/* -------------------------------------------------------------- */}
            {/* KPI glance (connected)                                         */}
            {/* -------------------------------------------------------------- */}
            {connected ? (
              <>
                <div className="border-b border-r border-border/60 bg-muted/25 px-4 py-2.5 md:px-5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    At a glance
                  </p>
                </div>
                <GridCells className={cn("grid-cols-1", solo ? "sm:grid-cols-2" : "sm:grid-cols-3")}>
                  <GridCell padded={false}>
                    <StatCard
                      variant="cell"
                      tone={asana?.needsReconnect ? "warning" : "success"}
                      icon={Link2}
                      label="Connection"
                      value={asana?.needsReconnect ? "Reconnect" : "Live"}
                      hint={
                        [asana?.account, asana?.workspace].filter(Boolean).join(" · ") ||
                        "Asana linked"
                      }
                      isLoading={status.isFetching && !status.data}
                    />
                  </GridCell>
                  <GridCell padded={false}>
                    <StatCard
                      variant="cell"
                      tone={projectReady ? "brand" : "warning"}
                      icon={FolderKanban}
                      label="Destination"
                      value={projectReady ? "Set" : "Needed"}
                      hint={
                        asana?.projectName ||
                        (projects.isLoading ? "Loading projects…" : "Choose a project")
                      }
                    />
                  </GridCell>
                  {solo ? null : (
                  <GridCell padded={false}>
                    <StatCard
                      variant="cell"
                      tone={
                        people.isLoading
                          ? "muted"
                          : unmatched > 0
                            ? "warning"
                            : "info"
                      }
                      icon={Users}
                      label="People matched"
                      value={
                        people.isLoading
                          ? "…"
                          : peopleList.length === 0
                            ? "—"
                            : `${matched}/${peopleList.length}`
                      }
                      hint={
                        people.isLoading
                          ? "Loading…"
                          : unmatched > 0
                            ? `${unmatched} still need a match`
                            : "Everyone covered"
                      }
                    />
                  </GridCell>
                  )}
                </GridCells>
              </>
            ) : null}

            {/* -------------------------------------------------------------- */}
            {/* Connection status strip                                        */}
            {/* -------------------------------------------------------------- */}
            <SectionLabel>Connection</SectionLabel>
            <div
              className={cn(
                "flex flex-wrap items-center gap-3 border-b border-r border-border/60 px-4 py-4 md:px-5",
                connected && !asana?.needsReconnect && "bg-success/5",
                connected && asana?.needsReconnect && "bg-warning/5",
                !connected && "bg-muted/20",
              )}
            >
              <span
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  connected && !asana?.needsReconnect && "bg-success-soft text-success",
                  connected && asana?.needsReconnect && "bg-warning-soft text-warning",
                  !connected && "bg-brand-soft text-brand",
                )}
              >
                {connected && !asana?.needsReconnect ? (
                  <CircleCheck className="h-5 w-5" />
                ) : connected ? (
                  <CircleAlert className="h-5 w-5" />
                ) : (
                  <Link2 className="h-5 w-5" />
                )}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">
                    {connected
                      ? asana?.needsReconnect
                        ? "Reconnect required"
                        : "Asana is connected"
                      : "Connect Asana to continue"}
                  </p>
                  {connected && !asana?.needsReconnect ? (
                    <Badge
                      variant="success"
                      className="text-[10px] uppercase tracking-wide"
                    >
                      Live
                    </Badge>
                  ) : null}
                  {asana?.needsReconnect ? (
                    <Badge
                      variant="warning"
                      className="text-[10px] uppercase tracking-wide"
                    >
                      Action needed
                    </Badge>
                  ) : null}
                </div>
                <p className="text-sm text-muted-foreground">
                  {connected
                    ? [asana?.account, asana?.workspace].filter(Boolean).join(" · ") ||
                      "Asana account linked."
                    : "Tasks are authored by the person who connects — not a shared bot account."}
                </p>
                {asana?.lastError ? (
                  <p className="text-xs text-destructive">{asana.lastError}</p>
                ) : null}
                {blocked ? <p className="text-xs text-muted-foreground">{blockedMessage}</p> : null}
              </div>
              {!connected ? (
                <Button onClick={connect} disabled={authorize.isPending || blocked}>
                  {authorize.isPending ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 h-4 w-4" />
                  )}
                  Connect Asana
                </Button>
              ) : null}
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Disconnected: setup story + empty                              */}
            {/* -------------------------------------------------------------- */}
            {showSetup ? (
              <>
                <SectionLabel>How it works</SectionLabel>
                <GridCells className={cn("grid-cols-1", solo ? "sm:grid-cols-2" : "sm:grid-cols-3")}>
                  {(solo ? SETUP_STEPS.filter((st) => st.title !== "People") : SETUP_STEPS).map((step) => (
                    <GridCell key={step.n} className="bg-background">
                      <div className="flex items-start gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-brand">
                          {step.n}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {step.title}
                          </p>
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {step.body}
                          </p>
                        </div>
                      </div>
                    </GridCell>
                  ))}
                </GridCells>
                <GridCell>
                  <EmptyState
                    icon={Link2}
                    title="No Asana account linked yet"
                    description={
                      solo
                        ? "Connect once and pick a project. Your meeting to-dos land there."
                        : "Connect once, pick a destination project, then match people so to-dos land on the right assignee."
                    }
                    action={
                      <Button onClick={connect} disabled={authorize.isPending || blocked}>
                        {authorize.isPending ? (
                          <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Link2 className="mr-2 h-4 w-4" />
                        )}
                        Connect Asana
                      </Button>
                    }
                  />
                </GridCell>
              </>
            ) : null}

            {/* -------------------------------------------------------------- */}
            {/* Connected: destination + people                                */}
            {/* -------------------------------------------------------------- */}
            {connected ? (
              <>
                <SectionLabel
                  hint={
                    asana?.scope === "company"
                      ? "Everyone in this company pushes here."
                      : "To-dos from your meetings land here."
                  }
                >
                  Destination
                </SectionLabel>
                {/* Workspace sits ABOVE the project on purpose: it decides which
                    projects exist at all, so choosing it second means choosing
                    from the wrong list first. */}
                <GridCell>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 flex h-10 shrink-0 items-center text-brand">
                        <Building2 className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 space-y-1">
                        <Label
                          htmlFor="asana-workspace"
                          className="text-sm font-semibold"
                        >
                          Workspace
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {canChangeWorkspace
                            ? `This account is in ${workspaceOptions.length} workspaces. Changing it clears the destination project and any people you have matched.`
                            : "To-dos go to this workspace. It is the only one on this Asana account."}
                        </p>
                      </div>
                    </div>
                    <div className="w-full sm:max-w-xs">
                      <Select
                        value={selectedWorkspaceId ?? undefined}
                        // Disabled rather than hidden when there is nothing to
                        // choose: the name still shows, so the cell answers
                        // "which workspace?" without pretending to offer a swap.
                        disabled={
                          !canChangeWorkspace ||
                          workspaces.isLoading ||
                          setWorkspace.isPending
                        }
                        onValueChange={(id) => void changeWorkspace(id)}
                      >
                        <SelectTrigger
                          id="asana-workspace"
                          className={compactSelectTriggerClass}
                        >
                          <SelectValue
                            placeholder={
                              workspaces.isLoading
                                ? "Loading workspaces…"
                                : (asana?.workspace ?? "Choose a workspace…")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {workspaceOptions.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.name || w.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {workspaces.isError ? (
                        <p className="mt-1.5 text-xs text-destructive">
                          Couldn&apos;t load workspaces. Press Refresh.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </GridCell>

                <GridCell className={cn(!projectReady && "bg-warning/[0.04]")}>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <span
                        className={cn(
                          "mt-0.5 flex h-10 shrink-0 items-center",
                          projectReady ? "text-brand" : "text-warning",
                        )}
                      >
                        <FolderKanban className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 space-y-1">
                        <Label htmlFor="asana-project" className="text-sm font-semibold">
                          Destination project
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {projectReady
                            ? asana?.projectName || "Project selected"
                            : "Required before anyone can push to-dos."}
                        </p>
                      </div>
                    </div>
                    <div className="w-full sm:max-w-xs">
                      <Select
                        value={asana?.projectId ?? undefined}
                        disabled={projects.isLoading || setProject.isPending}
                        onValueChange={(id) => {
                          const name = projectOptions.find((p) => p.id === id)?.name;
                          setProject.mutate({
                            projectId: id,
                            projectName: name ?? undefined,
                          });
                        }}
                      >
                        <SelectTrigger
                          id="asana-project"
                          className={compactSelectTriggerClass}
                        >
                          <SelectValue
                            placeholder={
                              projects.isLoading
                                ? "Loading projects…"
                                : "Choose a project…"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {projectOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name || p.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {projects.isError ? (
                        <p className="mt-1.5 text-xs text-destructive">
                          Couldn&apos;t load projects. Press Refresh.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </GridCell>

                {solo ? null : (
                  <>
                <SectionLabel
                  hint={
                    people.isLoading
                      ? "Loading matches…"
                      : hasUnsaved
                        ? `${dirtyIds.length} unsaved ${dirtyIds.length === 1 ? "change" : "changes"}`
                        : unmatched > 0
                          ? // Conditional, not a warning: a person only ever
                            // receives a task if a meeting they were IN assigns
                            // them one. The old wording read as "something is
                            // wrong now", which it is not. (05-08-2026)
                            `${unmatched} ${unmatched === 1 ? "person is" : "people are"} not matched — if a meeting assigns them a to-do, it will arrive unassigned.`
                          : "Everyone is matched. To-dos will land on the right person."
                  }
                  action={
                    hasUnsaved ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDraft({})}
                          disabled={saving}
                        >
                          Discard
                        </Button>
                        <Button size="sm" onClick={saveMappings} disabled={saving}>
                          {saving ? (
                            <>
                              <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
                              Saving…
                            </>
                          ) : (
                            "Save changes"
                          )}
                        </Button>
                      </div>
                    ) : null
                  }
                >
                  People
                </SectionLabel>

                <GridCell padded={false}>
                  {people.isLoading ? (
                    <SkeletonRows twoLine />
                  ) : people.isError ? (
                    <p className="px-4 py-10 text-sm text-destructive md:px-5">
                      Couldn&apos;t load people. Press Refresh and try again.
                    </p>
                  ) : peopleList.length === 0 ? (
                    <div className="px-4 py-6 md:px-5">
                      <EmptyState
                        icon={Users}
                        title="No company members to map"
                        description="To-dos will be matched by email until people appear here."
                      />
                    </div>
                  ) : (
                    <ul>
                      <li className="hidden border-b border-border/60 bg-muted/15 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground md:grid md:grid-cols-[minmax(0,1fr)_8.5rem_minmax(12rem,16rem)] md:items-center md:gap-3 md:px-5">
                        <span>Person</span>
                        <span>Match</span>
                        <span>Asana member</span>
                      </li>
                      {peopleList.map((p) => {
                        const pending = p.userId in draft && dirtyIds.includes(p.userId);
                        const gap = p.source === "none" && !pending;
                        return (
                          <li
                            key={p.userId}
                            className={cn(
                              "grid grid-cols-1 gap-3 border-b border-border/60 px-4 py-3.5 last:border-b-0 md:grid-cols-[minmax(0,1fr)_8.5rem_minmax(12rem,16rem)] md:items-center md:gap-3 md:px-5",
                              "transition-colors hover:bg-muted/20",
                              gap && "border-l-2 border-l-warning/70 bg-warning/[0.03]",
                              pending && "border-l-2 border-l-brand bg-brand-soft/25",
                            )}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              {/* Real photo when the person has one; initials are
                                  the fallback, never a stand-in for someone
                                  else's face. (05-08-2026) */}
                              {p.avatarUrl ? (
                                <img
                                  src={p.avatarUrl}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-background"
                                  // A signed URL expires. When it does, fall back
                                  // to initials rather than a broken-image glyph.
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand ring-2 ring-background">
                                  {initials(p.name, p.email)}
                                </span>
                              )}
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-foreground">
                                  {p.name || "Unnamed"}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {p.email || "No email"}
                                </p>
                              </div>
                            </div>
                            <div className="md:justify-self-start">
                              {pending ? (
                                <Badge
                                  variant="default"
                                  className="text-[10px] uppercase tracking-wide"
                                >
                                  Unsaved
                                </Badge>
                              ) : (
                                <MatchBadge source={p.source} />
                              )}
                            </div>
                            <Select
                              value={shownMatch(p) ?? MATCH_BY_EMAIL}
                              disabled={saving}
                              onValueChange={(value) => {
                                // Drafted only — committed by Save.
                                setDraft((d) => ({
                                  ...d,
                                  [p.userId]: value === MATCH_BY_EMAIL ? null : value,
                                }));
                              }}
                            >
                              <SelectTrigger
                                aria-label={`Asana member for ${p.name || p.email || "person"}`}
                                className={compactSelectTriggerClass}
                              >
                                <SelectValue placeholder="Match by email" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={MATCH_BY_EMAIL}>
                                  Match by email
                                </SelectItem>
                                {members.map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.name || m.email || m.id}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </GridCell>

                {hasUnsaved ? (
                  <GridCell padded={false} className="bg-brand-soft/40">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-5">
                      <p className="text-sm text-foreground">
                        <span className="font-semibold">{dirtyIds.length}</span>{" "}
                        {dirtyIds.length === 1 ? "match change" : "match changes"}{" "}
                        not saved yet.
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDraft({})}
                          disabled={saving}
                        >
                          Discard
                        </Button>
                        <Button size="sm" onClick={saveMappings} disabled={saving}>
                          {saving ? (
                            <>
                              <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
                              Saving…
                            </>
                          ) : (
                            "Save changes"
                          )}
                        </Button>
                      </div>
                    </div>
                  </GridCell>
                ) : null}
                  </>
                )}
              </>
            ) : null}
          </>
        )}

        {notice ? (
          <GridCell
            padded={false}
            className={cn(
              notice.toLowerCase().includes("could not") ||
                notice.toLowerCase().includes("couldn't")
                ? "bg-destructive/5"
                : "bg-success/5",
            )}
          >
            <p
              className={cn(
                "px-4 py-3 text-sm md:px-5",
                notice.toLowerCase().includes("could not") ||
                  notice.toLowerCase().includes("couldn't")
                  ? "text-destructive"
                  : "text-success",
              )}
            >
              {notice}
            </p>
          </GridCell>
        ) : null}
      </GridBlock>
    </div>
  );
}

export default AsanaIntegrationPage;
